import type { AppGroup } from '@/apps/types';

/**
 * What hovering a row in the All Programs cascade means for the open folder.
 *
 * This exists as its own function because getting it wrong is invisible: the
 * flyout renders its program rows with one helper and used it for both the
 * top-level list *and* the rows inside an open folder's submenu. Those two
 * cases want opposite answers, so every row of every submenu carried a handler
 * whose entire job was to close the submenu it lived in — and the item under
 * the pointer became unreachable.
 *
 * The invariant worth naming, and the one the tests hold to: **a row inside a
 * submenu never closes it.** It is the destination, not a competitor.
 */

export interface CascadeRow {
  /** The folder this row opens, or `null` for a program. */
  group: AppGroup | null;
  /** True when this row lives inside an open folder's submenu. */
  inSubmenu: boolean;
}

export type CascadeIntent =
  /** Drop any pending change: the pointer has arrived where it was heading. */
  | { kind: 'cancel' }
  /** Open this folder, once the pointer has held still long enough to mean it. */
  | { kind: 'open'; group: AppGroup }
  /** Close whichever folder is open, on the same delay. */
  | { kind: 'close' };

export function cascadeIntent(row: CascadeRow, openGroup: AppGroup | null): CascadeIntent {
  // The invariant, first and unconditionally. A submenu row is where the
  // diagonal was aiming; reaching it settles the question rather than reopening
  // it, whatever else is going on.
  if (row.inSubmenu) return { kind: 'cancel' };

  // A program in the flyout's own list is a sibling of the folders, so settling
  // on one means the reader is done with whichever folder was open.
  if (row.group === null) return { kind: 'close' };

  // Re-entering the folder that is already open — which is what happens when a
  // diagonal dips back onto its parent row — cancels rather than re-opening.
  if (row.group === openGroup) return { kind: 'cancel' };

  return { kind: 'open', group: row.group };
}
